import { SeoTargetKind } from '../../seo-brief.schema';
import type { SeoScoreContext } from '../context';
import type { SeoLink } from '../html-extract';
import { fail, pass, rangeStatus, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { normalizeFr, tokenizeWords } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';
import { externalSourcedFacts } from '../../seo-brief-facts';
import { hrefForms } from './brief.rules';
import { FR_WEAK_ANCHORS } from '../wordlists-fr';

const G = SeoScoreGroup.LINKS;

const WEAK = new Set(FR_WEAK_ANCHORS.map((a): string => normalizeFr(a)));

function normalizeHref(href: string): string {
  return href.trim().toLowerCase().replace(/#.*$/, '').replace(/\/+$/, '');
}

/**
 * Hôtes interdits de lien : ceux du profil (options) et ceux du brief (sites positionnés sur la
 * requête, concurrents surveillés). Un lien vers l'un d'eux envoie le lecteur chez un concurrent.
 */
function forbiddenHosts(ctx: SeoScoreContext): string[] {
  return [...(ctx.options.forbidden_hosts ?? []), ...(ctx.brief?.competitor_hosts ?? [])].map((h): string =>
    h.toLowerCase().replace(/^www\./, ''),
  );
}

function partition(ctx: SeoScoreContext): { internal: SeoLink[]; external: SeoLink[] } {
  const internal: SeoLink[] = [];
  const external: SeoLink[] = [];
  for (const link of ctx.doc.links) {
    const href = link.href.trim();
    if (href.length === 0 || /^(mailto|tel|sms|javascript):/i.test(href)) continue;
    if (ctx.isInternalHref(href)) internal.push(link);
    else external.push(link);
  }
  return { internal, external };
}

export function isWeakAnchor(anchor: string): boolean {
  const norm = normalizeFr(anchor)
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (norm.length === 0) return true;
  if (WEAK.has(norm)) return true;
  if (/^(https?:\/\/|www\.)/.test(anchor.trim().toLowerCase())) return true;
  const words = tokenizeWords(norm).length;
  return words > T.ANCHOR_WORDS[1];
}

export const LINKS_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.INTERNAL_LINKS_COUNT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { internal } = partition(ctx);
      const distinct = new Set(internal.map((l): string => normalizeHref(l.href))).size;
      const [lo, hi] = T.INTERNAL_LINKS[ctx.targetKind] ?? T.INTERNAL_LINKS.PROFESSIONAL;
      const threshold = `${lo}–${hi} liens internes distincts`;
      if (distinct === 0) return fail(0, threshold);
      if (distinct >= lo && distinct <= hi) return pass(distinct, threshold);
      return warn(distinct, threshold);
    },
  },
  {
    key: SeoRuleKey.LINK_DISTRIBUTION,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { internal } = partition(ctx);
      if (internal.length < 2 || ctx.doc.blocks.length < 6) return null;
      const cutoff = Math.floor(ctx.doc.blocks.length * 0.66);
      const late = internal.filter((l): boolean => l.blockIndex >= cutoff).length;
      const ratio = late / internal.length;
      if (ratio >= 1) return fail('tous dans le dernier tiers', 'répartis dans le corps');
      if (ratio >= T.LINKS_LAST_THIRD_WARN_RATIO)
        return warn(`${late}/${internal.length} dans le dernier tiers`, 'répartis dans le corps');
      return pass('répartis');
    },
  },
  {
    key: SeoRuleKey.ANCHOR_QUALITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { internal, external } = partition(ctx);
      const all = [...internal, ...external];
      if (all.length === 0) return null;
      const weak = all.filter((l): boolean => isWeakAnchor(l.anchor));
      const locations = weak.map((l): string => `« ${l.anchor || '(vide)'} » → ${l.href}`);
      if (weak.length === 0) return pass('100 % descriptives');
      if (weak.length === 1) return warn(1, '0 ancre faible', locations);
      return fail(weak.length, '0 ancre faible', locations);
    },
  },
  {
    key: SeoRuleKey.DUPLICATE_TARGET,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { internal, external } = partition(ctx);
      const all = [...internal, ...external];
      if (all.length === 0) return null;
      const counts = new Map<string, number>();
      for (const l of all) {
        const k = normalizeHref(l.href);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      const dup = [...counts.entries()].filter(([, c]): boolean => c > 1);
      const locations = dup.map(([href, c]): string => `${href} (${c}×)`);
      if (dup.length === 0) return pass(0, '0 doublon');
      if (dup.length === 1) return warn(1, '0 doublon', locations);
      return fail(dup.length, '0 doublon', locations);
    },
  },
  {
    key: SeoRuleKey.LINK_DENSITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { internal, external } = partition(ctx);
      const total = internal.length + external.length;
      if (total === 0 || ctx.wordCount === 0) return null;
      const per100 = (total / ctx.wordCount) * 100;
      const value = `${Math.round(per100 * 10) / 10} lien / 100 mots`;
      if (per100 <= T.LINK_DENSITY_PER_100.PASS) return pass(value, `≤ ${T.LINK_DENSITY_PER_100.PASS}`);
      if (per100 <= T.LINK_DENSITY_PER_100.WARN) return warn(value, `≤ ${T.LINK_DENSITY_PER_100.PASS}`);
      return fail(value, `≤ ${T.LINK_DENSITY_PER_100.PASS}`);
    },
  },
  {
    key: SeoRuleKey.CONVERSION_LINK,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const paths = ctx.options.conversion_paths ?? ctx.brief?.conversion_paths ?? [];
      if (paths.length === 0) return null;
      const { internal } = partition(ctx);
      // « / » désigne l'accueil seul (cible de l'appel à l'action Propelsia) : en fragment, il validerait tout lien.
      const hit = internal.find((l): boolean =>
        paths.some((p): boolean =>
          p === '/' ? hrefForms(l.href).path === '/' : l.href.toLowerCase().includes(p.toLowerCase()),
        ),
      );
      return hit ? pass('présent', undefined, [hit.href]) : fail('absent', paths.join(', '));
    },
  },
  {
    key: SeoRuleKey.EXTERNAL_SOURCE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.targetKind !== SeoTargetKind.PROPELSIA) return null;
      // Sans fait externe sourcé dans le brief, le rédacteur n'a aucun lien externe autorisé :
      // la règle serait morte (55 échecs sur 109 au bench). Elle ne s'évalue que si un lien est possible.
      if (ctx.brief && externalSourcedFacts(ctx.brief, ctx.options.internal_hosts ?? []).length === 0) return null;
      const { external } = partition(ctx);
      const forbidden = forbiddenHosts(ctx);
      const ok = external.filter((l): boolean => {
        const host = ctx.hrefHost(l.href);
        return !!host && !forbidden.some((f): boolean => host === f || host.endsWith(`.${f}`));
      });
      return ok.length > 0 ? pass(ok.length, 'bonus') : fail(0, 'bonus');
    },
  },
  {
    key: SeoRuleKey.FORBIDDEN_LINK,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const forbidden = forbiddenHosts(ctx);
      if (forbidden.length === 0) return null;
      const bad = ctx.doc.links.filter((l): boolean => {
        const host = ctx.hrefHost(l.href);
        return !!host && forbidden.some((f): boolean => host === f || host.endsWith(`.${f}`));
      });
      if (bad.length === 0) return pass(0, '0');
      return fail(
        bad.length,
        '0',
        bad.map((l): string => l.href),
      );
    },
  },
];

/** Exposé pour les stats globales. */
export function linkCounts(ctx: SeoScoreContext): { internal: number; external: number } {
  const { internal, external } = partition(ctx);
  return { internal: internal.length, external: external.length };
}

/** Utilitaire de test : statut d'une plage sur un nombre de liens. */
export const linkRange = rangeStatus;
